import { useEffect, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { Panel } from "@/components/PageHeader";
import { api, eur, num, errMsg } from "@/lib/api";

const RANGES = [["6", "6 mesi"], ["12", "12 mesi"], ["24", "24 mesi"]];
const pct = (v) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(1).replace(".", ",")}%`);

export function StoreGrowth({ id }) {
  const [months, setMonths] = useState("12");
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { setD(null); setError(""); api.get(`/stores/${id}/growth`, { params: { months } }).then((r) => setD(r.data)).catch((e) => setError(errMsg(e))); }, [id, months]);

  return (
    <Panel title="Crescita mese per mese vs anno precedente" testId="store-growth" action={
      <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5">{RANGES.map(([k, l]) => <button type="button" key={k} data-testid={`store-growth-range-${k}`} onClick={() => setMonths(k)} className={`rounded-full px-3 py-1 text-xs ${months === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>)}</div>}>
      {error && <div className="m-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="store-growth-error">{error}</div>}
      {!d && !error && <Skeleton className="m-4 h-64" />}
      {d && (
        <div className="p-3">
          <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[["Periodo", eur(d.total_current)], ["Anno precedente", eur(d.total_previous)], ["Variazione", pct(d.growth)], ["Mesi in crescita", `${d.months_up} / ${d.months_up + d.months_down}`]].map(([l, v], i) => (
              <div key={l} className="rounded-lg bg-slate-50 p-3" data-testid={`store-growth-kpi-${i}`}><div className="text-[11px] uppercase tracking-wider text-slate-500">{l}</div>
                <div className={`mt-1 font-heading text-lg font-semibold ${l === "Variazione" ? (d.growth === null ? "text-slate-400" : d.growth >= 0 ? "text-emerald-600" : "text-rose-600") : "text-slate-900"}`}>{v}</div></div>))}
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <ComposedChart data={d.rows}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
                <YAxis yAxisId="eur" tickLine={false} axisLine={false} fontSize={10} width={70} tickFormatter={(v) => num(v)} />
                <YAxis yAxisId="pct" orientation="right" tickLine={false} axisLine={false} fontSize={10} width={45} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v, name) => (name === "Variazione %" ? pct(v) : eur(v))} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="eur" dataKey="previous" name="Anno precedente" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
                <Bar yAxisId="eur" dataKey="current" name="Periodo" fill="#2563eb" radius={[3, 3, 0, 0]} />
                <Line yAxisId="pct" type="monotone" dataKey="growth" name="Variazione %" stroke="#d97706" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 overflow-auto">
            <table className="w-full text-xs" data-testid="store-growth-table">
              <thead className="text-left text-slate-500"><tr><th className="px-2 py-1 font-medium">Mese</th><th className="px-2 py-1 text-right font-medium">Vendite</th><th className="px-2 py-1 text-right font-medium">Anno prec.</th><th className="px-2 py-1 text-right font-medium">Var.</th><th className="px-2 py-1 text-right font-medium">Scontrini</th></tr></thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.month} className="border-t border-slate-100" data-testid={`store-growth-row-${r.month}`}>
                    <td className="px-2 py-1 capitalize">{r.label}</td><td className="px-2 py-1 text-right font-mono">{eur(r.current)}</td><td className="px-2 py-1 text-right font-mono text-slate-500">{eur(r.previous)}</td>
                    <td className="px-2 py-1 text-right">{r.growth === null ? <span className="text-slate-300">—</span> : <span className={`rounded px-1.5 py-0.5 font-mono ${r.growth >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{pct(r.growth)}</span>}</td>
                    <td className="px-2 py-1 text-right font-mono">{num(r.receipts)}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        </div>)}
    </Panel>
  );
}
